import { parse } from "date-fns/parse";
import { isValid } from "date-fns/isValid";
import { formatDistanceStrict } from "date-fns/formatDistanceStrict";
import { toZonedTime, format, fromZonedTime } from "date-fns-tz";

/**
 * Set of strings used to format dates with date-fns
 * @ignore
 */

const dateFormats = {
  full: "EEE, MMM d, yyyy, h:mm aaaa z",
  short: "MMM do, yyyy",
  shortWithWeekday: "EEE, MMM d, yyyy",
  yearMonthDay: "yyyy-MM-dd",
  monthDayYear: "MM/dd/yyyy",
  monthDayYearWithTime: "MM/dd/yyyy h:mm aaaa",
  shortTime: "h:mm",
  shortDateTime: "MMM d, yyyy h:mm aaaa",
  longDate: "MMMM do, yyyy",
  longDateTime: "MMMM do, yyyy h:mm aaaa",
  time24hr: "HH:mm"
};

/**
 * Used to get a formatted string from a given date
 */
const getFormattedDateString = (date, dateFormat, timeZone) => {
  if (!dateFormat) {
    throw new Error("dateFormat must be provided in order to format the date object.");
  }
  const dateObject = new Date(date);
  if (!date || !isValid(dateObject)) {
    return "";
  }
  return format(dateObject, dateFormat, {
    timeZone
  });
};

/**
 * @returns Returns a string formatted as "January 1st, 2019"
 */
export const getFormattedDateStringLongDateTime = (date, timeZone) => {
  return getFormattedDateString(date, dateFormats.longDateTime, timeZone);
};

/**
 * @returns Returns a string formatted as "January 1st, 2019 1:23 PM"
 */
export const getFormattedDateStringLongDate = (date, timeZone) => {
  return getFormattedDateString(date, dateFormats.longDate, timeZone);
};

/**
 * @returns Returns a string formatted as "2019-10-01"
 */
export const getFormattedDateStringMonthDayYear = (date, timeZone) => {
  return getFormattedDateString(date, dateFormats.monthDayYear, timeZone);
};

/**
 * @returns Returns a string formatted as "2019-10-01 1:23 PM"
 */
export const getFormattedDateStringMonthDayYearWithTime = (date, timeZone) => {
  return getFormattedDateString(date, dateFormats.monthDayYearWithTime, timeZone);
};

/**
 * @returns Returns a string formatted as "Oct 1, 2019 2:59 PM"
 */
export const getFormattedDateStringShortDateTime = (date, timeZone) => {
  return getFormattedDateString(date, dateFormats.shortDateTime, timeZone);
};

/**
 * @returns Returns a string formatted as "Tue, Oct 1, 2019 2:59 PM CDT"
 */
export const getFormattedDateStringFull = (date, timeZone) => {
  return getFormattedDateString(date, dateFormats.full, timeZone);
};

/**
 * @returns Returns a string formatted as "Oct 1, 2019"
 */
export const getFormattedDateStringShort = (date, timeZone) => {
  return getFormattedDateString(date, dateFormats.short, timeZone);
};

/**
 * @returns Returns a string formatted as "Tue, Oct 1, 2019"
 */
export const getFormattedDateStringShortWithWeekday = (date, timeZone) => {
  return getFormattedDateString(date, dateFormats.shortWithWeekday, timeZone);
};

/**
 * @returns Returns a string formatted as "2019-10-01"
 */
export const getFormattedDateStringYearMonthDay = (date, timeZone) => {
  return getFormattedDateString(date, dateFormats.yearMonthDay, timeZone);
};

/**
 * @returns Returns a string formatted as "2:59", between 1-12.
 */
export const getFormattedDateStringShortTime = (date, timeZone) => {
  return getFormattedDateString(date, dateFormats.shortTime, timeZone);
};

/**
 * @returns Returns a string formatted as "18:59".
 */
export const getFormattedDateStringTime24hr = (date, timeZone) => {
  return getFormattedDateString(date, dateFormats.time24hr, timeZone);
};

/**
 * Gets text such as "4 days ago" relative to two dates. The second date defaults to today if none is provided.
 * The order in which these are supplied is important to whether you get ending text such as "ago", or "from now".
 */
export const getFormattedDateStringRelative = (start, end = new Date()) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (!startDate || !isValid(startDate) || !isValid(endDate)) {
    return "";
  }
  return formatDistanceStrict(startDate, endDate, {
    addSuffix: true
  });
};

/**
 * @returns Returns a string representation of the format string provided.
 */
export const formatDate = (date, dateFormat) => {
  const dateObject = new Date(date);
  if (!isValid(dateObject)) {
    return "";
  }
  return format(dateObject, dateFormat);
};

/** */
export const parseDate = (str, format) => {
  const parsed = parse(str, format, new Date());
  if (isValid(parsed)) {
    return parsed;
  }
  return undefined;
};

/**
 * Converts the given Date or string into an ISO string.
 */
export const convertDateTimeValueToUtc = value => {
  const date = new Date(value);
  if (!isValid(date)) {
    return "";
  }

  // Date().toISOString() returns "YYYY-MM-DDTHH:mm:ss.xxxxZ"
  // Headless implementation only accepts "YYYY-MM-DDTHH:mm:ssZ"
  // Get the user timezone from the Intl APIs
  return fromZonedTime(date, Intl.DateTimeFormat().resolvedOptions().timeZone).toISOString().replace(/.\d{3}Z/, "Z");
};

/* The ISO dates in JS follow a derived version of the ISO8601 spec. Noda Time uses a more direct version  */
// https://stackoverflow.com/a/23829390/299408
export const convertDateToISO8601String = date => {
  function pad(n) {
    return n < 10 ? "0" + n : n;
  }
  if (!(date instanceof Date)) {
    date = new Date(date);
  }
  return date.getUTCFullYear() + "-" + pad(date.getUTCMonth() + 1) + "-" + pad(date.getUTCDate()) + "T" + pad(date.getUTCHours()) + ":" + pad(date.getUTCMinutes()) + ":" + pad(date.getUTCSeconds()) + "Z";
};

/** */
export const toDateTimeZone = (date, timeZone = "", dateOnly = false) => {
  // Convert the given date to an ISO string
  const dateISOString = new Date(date).toISOString();

  // The ISO string will then be converted to a Date object in the given timezone
  // And from that, we will then give it to the formatter to get the desired string.
  if (dateOnly) {
    return getFormattedDateStringMonthDayYear(toZonedTime(dateISOString, timeZone), timeZone);
  }
  return getFormattedDateStringMonthDayYearWithTime(toZonedTime(dateISOString, timeZone), timeZone);
};

/**
 * @ignore
 * @deprecated Please switch to use the new function getFormattedDateStringMonthDayYear */
export const toNumericUs = date => {
  //eslint-disable-next-line no-console
  console.warn('DEPRECATED. Please switch to use the new function "getFormattedDateStringMonthDayYear".');
  return getFormattedDateStringMonthDayYear(date);
};
/**
 * @ignore
 * @deprecated Please switch to use the new function getFormattedDateStringRelative */
export const formatDistanceString = (from, now, timeZone) => {
  //eslint-disable-next-line no-console
  console.warn('DEPRECATED. Please switch to use the new function "getFormattedDateStringRelative".');
  return getFormattedDateStringRelative(from, now);
};
/** */
export const convertUtcDateToLocalDateWithoutTime = date => {
  const dateWithoutTimezone = new Date(date);
  // Ensure that the time is not offset when converting that string to a Date object: https://stackoverflow.com/a/52352512
  // (Convert to the same time in the current timezone -- undo offset)
  return new Date(dateWithoutTimezone.valueOf() + dateWithoutTimezone.getTimezoneOffset() * 60 * 1000);
};